import { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';

import { parseResponseError } from 'common/helpers';
import { useLoader, useStore } from 'common/hooks';
import { CompanyMaster } from 'models/company/Company';
import {
    deleteCompanyPort,
    getAllProfiles,
    getCompanyGuiApplications,
    getCompanyPorts,
    saveCompanyPort,
    saveProfile
} from 'data/fguiPort/datasources/fguiPort.datasource';
import {
    CompanyFguiPort,
    FguiApplication,
    FguiPortProfile
} from 'data/fguiPort/schemas/fguiPort.schema';

export const EMPTY_PROFILE: FguiPortProfile = {
    profile_id: '',
    profile_name: '',
    port_type: 'standard',
    sort_order: 0,
    status: 'active'
};

export type CompanyPortForm = {
    profile_id: string;
    mapping_key: string; // `${mapping_id}|${application_id}|${platform_id}`
    host: string;
    port: string;
    status: 'active' | 'inactive';
};

export const EMPTY_COMPANY_PORT: CompanyPortForm = {
    profile_id: '',
    mapping_key: '',
    host: '',
    port: '',
    status: 'active'
};

export const toMappingKey = (app: Pick<FguiApplication, 'mapping_id' | 'application_id' | 'platform_id'>) =>
    `${app.mapping_id}|${app.application_id}|${app.platform_id}`;

const errorMessage = (error: unknown) =>
    parseResponseError(error)?.message ?? (error as Error).message;

function FguiPortManagementViewModel() {
    const loader = useLoader();
    const { userCompanyStore, companyMasterStore } = useStore();
    const { userRole } = userCompanyStore;
    const { companyMaster } = companyMasterStore;

    // Port names
    const [profiles, setProfiles] = useState<FguiPortProfile[]>([]);
    const [profileForm, setProfileForm] = useState<FguiPortProfile>(EMPTY_PROFILE);
    const [isEditingProfile, setIsEditingProfile] = useState(false);

    // Company ports
    const [selectedCompany, setSelectedCompany] = useState<CompanyMaster>();
    const [companyPorts, setCompanyPorts] = useState<CompanyFguiPort[]>([]);
    const [guiApplications, setGuiApplications] = useState<FguiApplication[]>([]);
    const [companyPortForm, setCompanyPortForm] = useState<CompanyPortForm>(EMPTY_COMPANY_PORT);

    const fetchProfiles = useCallback(async () => {
        const { data } = await getAllProfiles();
        setProfiles(data.result_list ?? []);
    }, []);

    const fetchCompany = useCallback(async (sid: string) => {
        const [ports, apps] = await Promise.all([getCompanyPorts(sid), getCompanyGuiApplications(sid)]);
        setCompanyPorts(ports.data.result_list ?? []);
        setGuiApplications(apps.data.result_list ?? []);
    }, []);

    useEffect(() => {
        if (userRole !== 'Super Admin') return;

        companyMasterStore.fetchCompanyMasterStore('', 'active');

        (async () => {
            try {
                loader.show();
                await fetchProfiles();
            } catch (error) {
                Swal.fire('Error', errorMessage(error), 'error');
            } finally {
                loader.hide();
            }
        })();
    }, [userRole]);

    // Port names

    const handleEditProfile = (profile: FguiPortProfile) => {
        setProfileForm(profile);
        setIsEditingProfile(true);
    };

    const handleResetProfile = () => {
        setProfileForm(EMPTY_PROFILE);
        setIsEditingProfile(false);
    };

    const handleSaveProfile = async () => {
        if (!profileForm.profile_id.trim() || !profileForm.profile_name.trim()) {
            Swal.fire('แจ้งเตือน', 'กรุณากรอก Port ID และชื่อพอร์ต', 'warning');
            return;
        }

        try {
            loader.show();
            await saveProfile(profileForm);
            await fetchProfiles();
            handleResetProfile();
        } catch (error) {
            Swal.fire('Error', errorMessage(error), 'error');
        } finally {
            loader.hide();
        }
    };

    // Company ports

    const handleSelectCompany = async (company: CompanyMaster | null) => {
        setSelectedCompany(company ?? undefined);
        setCompanyPortForm(EMPTY_COMPANY_PORT);
        setCompanyPorts([]);
        setGuiApplications([]);
        if (!company) return;

        try {
            loader.show();
            await fetchCompany(company.sid);
        } catch (error) {
            Swal.fire('Error', errorMessage(error), 'error');
        } finally {
            loader.hide();
        }
    };

    const handleEditCompanyPort = (companyPort: CompanyFguiPort) => {
        setCompanyPortForm({
            profile_id: companyPort.profile_id,
            mapping_key: toMappingKey(companyPort),
            host: companyPort.host,
            port: companyPort.port,
            status: companyPort.status
        });
    };

    // Picking an app pre-fills its current host / port as a starting point
    const handleSelectApplication = (app: FguiApplication | null) => {
        setCompanyPortForm(prev => ({
            ...prev,
            mapping_key: app ? toMappingKey(app) : '',
            host: prev.host || app?.application_host || '',
            port: prev.port || app?.application_port || ''
        }));
    };

    const handleSaveCompanyPort = async () => {
        const app = guiApplications.find(a => toMappingKey(a) === companyPortForm.mapping_key);
        if (!selectedCompany || !companyPortForm.profile_id || !app) {
            Swal.fire('แจ้งเตือน', 'กรุณาเลือกพอร์ตและแอป GUI', 'warning');
            return;
        }
        if (!companyPortForm.host.trim() || !companyPortForm.port.trim()) {
            Swal.fire('แจ้งเตือน', 'กรุณากรอก IP และ Port', 'warning');
            return;
        }

        try {
            loader.show();
            await saveCompanyPort({
                sid: selectedCompany.sid,
                profile_id: companyPortForm.profile_id,
                mapping_id: app.mapping_id,
                application_id: app.application_id,
                platform_id: app.platform_id,
                host: companyPortForm.host.trim(),
                port: companyPortForm.port.trim(),
                status: companyPortForm.status
            });
            await fetchCompany(selectedCompany.sid);
            setCompanyPortForm(EMPTY_COMPANY_PORT);
        } catch (error) {
            Swal.fire('Error', errorMessage(error), 'error');
        } finally {
            loader.hide();
        }
    };

    const handleDeleteCompanyPort = async (companyPort: CompanyFguiPort) => {
        const result = await Swal.fire({
            title: `ลบการตั้งค่าพอร์ต ${companyPort.profile_name ?? companyPort.profile_id}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'ลบ',
            cancelButtonText: 'ยกเลิก'
        });
        if (!result.isConfirmed || !selectedCompany) return;

        try {
            loader.show();
            await deleteCompanyPort(companyPort.sid, companyPort.profile_id);
            await fetchCompany(selectedCompany.sid);
        } catch (error) {
            Swal.fire('Error', errorMessage(error), 'error');
        } finally {
            loader.hide();
        }
    };

    return {
        userRole,
        companyMaster,
        profiles,
        profileForm,
        isEditingProfile,
        setProfileForm,
        handleEditProfile,
        handleResetProfile,
        handleSaveProfile,
        selectedCompany,
        companyPorts,
        guiApplications,
        companyPortForm,
        setCompanyPortForm,
        handleSelectCompany,
        handleEditCompanyPort,
        handleSelectApplication,
        handleSaveCompanyPort,
        handleDeleteCompanyPort
    };
}

export default FguiPortManagementViewModel;
